import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Keycloak, { KeycloakInitOptions, KeycloakTokenParsed } from 'keycloak-js'
import * as Sentry from '@sentry/react'
import { AppError, errors } from '~/prix'
import keycloakConfig from '~/utils/shared/keycloakConfig'
import { clear } from '~/prix/utils/browserCache'

export interface KeycloakProviderProps {
  keycloak: Keycloak
  autoRefreshOnExpire?: boolean
  autoRefreshBeforeExpire?: boolean
  refreshSecondsBeforeExpire?: number
  checkTokenSeconds?: number
  initOptions?: KeycloakInitOptions
  children: React.ReactNode
}

export type KeycloakContext = {
  login: () => Promise<void>
  logout: () => Promise<void>
  keycloak: Keycloak
  isLoading: boolean
  bypassAuth: boolean
  isAuthenticated: boolean | null
  error: AppError | null
  token: string | null
  tokenParsed: KeycloakTokenParsed | null
  initError: AppError | null
  authError: AppError | null
  authRefreshError: AppError | null
}

const bypassAuth = process.env.BYPASS_AUTH as unknown as boolean
const keycloakContext = createContext<KeycloakContext>({
  login: async () => {},
  logout: async () => {},
  keycloak: null as any,
  isLoading: false,
  bypassAuth,
  isAuthenticated: null,
  error: null,
  token: null,
  tokenParsed: null,
  initError: null,
  authError: null,
  authRefreshError: null,
})

const bypassAuthPayload: KeycloakTokenParsed = {
  sub: 'bypass',
  email: 'bypass@prix.tech',
  name: 'Bypass',
  email_verified: true,
  resource_access: {
    'prix-tech': {
      roles: ['ADMINISTRADOR'],
    },
  },
  uf: 'SC',
  upn: '12345678901',
}

const defaultInitOptions: KeycloakInitOptions = {
  onLoad: 'check-sso',
  pkceMethod: 'S256',
  checkLoginIframe: false,
}

const thirdPartyCheckIframeTimeoutMessage = 'Timeout when waiting for 3rd party check iframe message'
const keycloakAuthenticationFallbackMessage =
  'Não foi possível concluir a autenticação com o AMEI. Verifique sua conexão e tente acessar novamente.'
const keycloakAuthenticationDeniedMessage =
  'O AMEI não autorizou a autenticação. Tente acessar novamente ou solicite acesso se o problema continuar.'
const keycloakAuthenticationReturnedErrorMessage =
  'O AMEI retornou um erro durante a autenticação. Tente acessar novamente.'
const keycloakSessionCheckFallbackMessage =
  'Não foi possível verificar automaticamente sua sessão do AMEI. Acesse novamente para continuar.'
const keycloakRefreshErrorMessage =
  'Sua sessão AMEI expirou ou não pôde ser renovada. Acesse novamente.'
const keycloakInitializationErrorMessage =
  'Não foi possível iniciar a autenticação com o AMEI. Recarregue a página e tente novamente.'
const safeKeycloakErrorFieldNames = ['error', 'error_description', 'message', 'reason', 'name'] as const
type SanitizedKeycloakErrorPayload = Record<string, string | number | boolean | null>
type KeycloakTokenRequestStage = 'authorization_code_exchange' | 'refresh_token' | 'unknown'
type KeycloakTokenRequestOutcome =
  | 'success'
  | 'http_error'
  | 'network_error'
  | 'timeout'
  | 'abort'
  | 'unknown'
type KeycloakTokenRequestBodyType =
  | 'array_buffer'
  | 'blob'
  | 'document'
  | 'form_data'
  | 'null'
  | 'string'
  | 'typed_array'
  | 'unknown'
  | 'url_search_params'
type KeycloakTokenRequestFailureEventType = 'abort' | 'error' | 'timeout'
type KeycloakTokenRequestRecordedEventType =
  | 'abort'
  | 'error'
  | 'loadend'
  | 'readystatechange'
  | 'timeout'
type KeycloakTokenRequestStatusCategory =
  | '2xx'
  | '3xx'
  | '4xx'
  | '5xx'
  | 'other'
  | 'status_0'
  | 'unknown'
type KeycloakTokenRequestDetails = {
  host: string
  isAmeiHost: boolean
  isConfiguredTokenEndpoint: boolean
  method: string | null
  path: string
}
type KeycloakTokenRequestTelemetry = {
  bodyType: KeycloakTokenRequestBodyType
  browserOnline: boolean | null
  durationMilliseconds: number | null
  failureEventType: KeycloakTokenRequestFailureEventType | null
  finishedAt: string | null
  hasRequestBody: boolean
  host: string | null
  isAmeiHost: boolean
  isConfiguredTokenEndpoint: boolean
  method: string | null
  outcome: KeycloakTokenRequestOutcome
  path: string | null
  readyState: number | null
  recordedEventType: KeycloakTokenRequestRecordedEventType | null
  responseType: string | null
  stage: KeycloakTokenRequestStage
  startedAt: string
  status: number | null
  statusCategory: KeycloakTokenRequestStatusCategory
  timeoutMilliseconds: number | null
  visibilityState: string | null
  withCredentials: boolean | null
}

const keycloakTokenEndpointPathSuffix = `/realms/${keycloakConfig.realm}/protocol/openid-connect/token`

function getKeycloakInitContext(initOptions: KeycloakInitOptions) {
  return {
    onLoad: initOptions.onLoad ?? null,
    checkLoginIframe: initOptions.checkLoginIframe ?? null,
    hasSilentCheckSsoRedirectUri: Boolean(initOptions.silentCheckSsoRedirectUri),
    pkceMethod: initOptions.pkceMethod ?? null,
    messageReceiveTimeout: initOptions.messageReceiveTimeout ?? null,
  }
}

function getKeycloakTokenRequestDetails(
  method: string,
  requestUrlInput: string | URL,
): KeycloakTokenRequestDetails | null {
  try {
    const configuredKeycloakUrl = new URL(keycloakConfig.url)
    const requestUrl = new URL(String(requestUrlInput), window.location.origin)
    const isAmeiHost = requestUrl.host === configuredKeycloakUrl.host
    const isConfiguredTokenEndpoint =
      isAmeiHost && requestUrl.pathname.endsWith(keycloakTokenEndpointPathSuffix)

    if (!isConfiguredTokenEndpoint) {
      return null
    }

    return {
      host: requestUrl.host,
      isAmeiHost,
      isConfiguredTokenEndpoint,
      method: method.toUpperCase().slice(0, 20),
      path: requestUrl.pathname,
    }
  } catch {
    return null
  }
}

function getKeycloakTokenRequestStageFromGrantType(
  grantType: unknown,
): KeycloakTokenRequestStage {
  if (grantType === 'authorization_code') {
    return 'authorization_code_exchange'
  }

  if (grantType === 'refresh_token') {
    return 'refresh_token'
  }

  return 'unknown'
}

function getKeycloakTokenRequestStage(
  body: Document | XMLHttpRequestBodyInit | null | undefined,
): KeycloakTokenRequestStage {
  try {
    if (typeof body === 'string') {
      if (body.includes('grant_type=authorization_code')) {
        return 'authorization_code_exchange'
      }

      if (body.includes('grant_type=refresh_token')) {
        return 'refresh_token'
      }

      return 'unknown'
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return getKeycloakTokenRequestStageFromGrantType(body.get('grant_type'))
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return getKeycloakTokenRequestStageFromGrantType(body.get('grant_type'))
    }
  } catch {
    return 'unknown'
  }

  return 'unknown'
}

function getKeycloakTokenRequestBodyType(
  body: Document | XMLHttpRequestBodyInit | null | undefined,
): KeycloakTokenRequestBodyType {
  try {
    if (body == null) {
      return 'null'
    }

    if (typeof body === 'string') {
      return 'string'
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return 'url_search_params'
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return 'form_data'
    }

    if (typeof Blob !== 'undefined' && body instanceof Blob) {
      return 'blob'
    }

    if (typeof Document !== 'undefined' && body instanceof Document) {
      return 'document'
    }

    if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
      return 'array_buffer'
    }

    if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(body)) {
      return 'typed_array'
    }
  } catch {
    return 'unknown'
  }

  return 'unknown'
}

function getKeycloakTokenRequestOutcome(
  status: number | null,
  failureEventType: KeycloakTokenRequestFailureEventType | null,
): KeycloakTokenRequestOutcome {
  if (failureEventType === 'abort') {
    return 'abort'
  }

  if (failureEventType === 'timeout') {
    return 'timeout'
  }

  if (failureEventType === 'error') {
    return 'network_error'
  }

  if (status == null) {
    return 'unknown'
  }

  if (status >= 200 && status < 300) {
    return 'success'
  }

  if (status === 0) {
    return 'network_error'
  }

  if (status >= 400) {
    return 'http_error'
  }

  return 'unknown'
}

function getKeycloakTokenRequestStatusCategory(
  status: number | null,
): KeycloakTokenRequestStatusCategory {
  if (status == null) {
    return 'unknown'
  }

  if (status === 0) {
    return 'status_0'
  }

  if (status >= 200 && status < 300) {
    return '2xx'
  }

  if (status >= 300 && status < 400) {
    return '3xx'
  }

  if (status >= 400 && status < 500) {
    return '4xx'
  }

  if (status >= 500 && status < 600) {
    return '5xx'
  }

  return 'other'
}

function getSafeNumber(getValue: () => number): number | null {
  try {
    const value = getValue()
    return Number.isFinite(value) ? value : null
  } catch {
    return null
  }
}

function getSafeBoolean(getValue: () => boolean): boolean | null {
  try {
    return getValue()
  } catch {
    return null
  }
}

function getSafeString(getValue: () => string): string | null {
  try {
    return getValue()
  } catch {
    return null
  }
}

function getBrowserOnline() {
  if (typeof navigator === 'undefined') {
    return null
  }

  return getSafeBoolean(() => navigator.onLine)
}

function getDocumentVisibilityState() {
  if (typeof document === 'undefined') {
    return null
  }

  return getSafeString(() => document.visibilityState)
}

function installKeycloakTokenRequestTelemetry(
  telemetryReference: React.MutableRefObject<KeycloakTokenRequestTelemetry | null>,
) {
  if (typeof window === 'undefined' || typeof XMLHttpRequest === 'undefined') {
    return () => {}
  }

  const originalOpen = XMLHttpRequest.prototype.open
  const originalSend = XMLHttpRequest.prototype.send
  const tokenRequestDetailsByRequest = new WeakMap<XMLHttpRequest, KeycloakTokenRequestDetails>()
  const bodyTypeByRequest = new WeakMap<XMLHttpRequest, KeycloakTokenRequestBodyType>()
  const failureEventTypeByRequest = new WeakMap<
    XMLHttpRequest,
    KeycloakTokenRequestFailureEventType
  >()
  const hasBodyByRequest = new WeakMap<XMLHttpRequest, boolean>()
  const listenerRegistrationByRequest = new WeakSet<XMLHttpRequest>()
  const stageByRequest = new WeakMap<XMLHttpRequest, KeycloakTokenRequestStage>()
  const startedAtByRequest = new WeakMap<XMLHttpRequest, string>()
  const startedAtTimeByRequest = new WeakMap<XMLHttpRequest, number>()

  const recordTelemetry = (
    request: XMLHttpRequest,
    recordedEventType: KeycloakTokenRequestRecordedEventType,
  ) => {
    try {
      const details = tokenRequestDetailsByRequest.get(request)

      if (!details) {
        return
      }

      const startedAt = startedAtByRequest.get(request) ?? new Date().toISOString()
      const startedAtTime = startedAtTimeByRequest.get(request)
      const finishedAtTime = Date.now()
      const failureEventType = failureEventTypeByRequest.get(request) ?? null
      const status = getSafeNumber(() => request.status)

      telemetryReference.current = {
        bodyType: bodyTypeByRequest.get(request) ?? 'unknown',
        browserOnline: getBrowserOnline(),
        durationMilliseconds:
          startedAtTime == null ? null : Math.max(finishedAtTime - startedAtTime, 0),
        failureEventType,
        finishedAt: new Date(finishedAtTime).toISOString(),
        hasRequestBody: hasBodyByRequest.get(request) ?? false,
        host: details.host,
        isAmeiHost: details.isAmeiHost,
        isConfiguredTokenEndpoint: details.isConfiguredTokenEndpoint,
        method: details.method,
        outcome: getKeycloakTokenRequestOutcome(status, failureEventType),
        path: details.path,
        readyState: getSafeNumber(() => request.readyState),
        recordedEventType,
        responseType: getSafeString(() => request.responseType),
        stage: stageByRequest.get(request) ?? 'unknown',
        startedAt,
        status,
        statusCategory: getKeycloakTokenRequestStatusCategory(status),
        timeoutMilliseconds: getSafeNumber(() => request.timeout),
        visibilityState: getDocumentVisibilityState(),
        withCredentials: getSafeBoolean(() => request.withCredentials),
      }
    } catch {
      // A telemetria nunca deve interferir no fluxo de autenticação.
    }
  }

  const registerListeners = (request: XMLHttpRequest) => {
    if (listenerRegistrationByRequest.has(request)) {
      return
    }

    listenerRegistrationByRequest.add(request)

    request.addEventListener('readystatechange', () => {
      try {
        if (request.readyState === XMLHttpRequest.DONE) {
          recordTelemetry(request, 'readystatechange')
        }
      } catch {
        // A telemetria nunca deve interferir no fluxo de autenticação.
      }
    })
    request.addEventListener('error', () => {
      failureEventTypeByRequest.set(request, 'error')
      recordTelemetry(request, 'error')
    })
    request.addEventListener('timeout', () => {
      failureEventTypeByRequest.set(request, 'timeout')
      recordTelemetry(request, 'timeout')
    })
    request.addEventListener('abort', () => {
      failureEventTypeByRequest.set(request, 'abort')
      recordTelemetry(request, 'abort')
    })
    request.addEventListener('loadend', () => {
      recordTelemetry(request, 'loadend')
    })
  }

  const patchedOpen = function patchedOpen(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
  ) {
    try {
      const requestDetails = getKeycloakTokenRequestDetails(method, url)

      if (requestDetails) {
        tokenRequestDetailsByRequest.set(this, requestDetails)
        registerListeners(this)
      } else {
        tokenRequestDetailsByRequest.delete(this)
      }
    } catch {
      tokenRequestDetailsByRequest.delete(this)
    }

    return originalOpen.apply(this, arguments as any)
  } as typeof XMLHttpRequest.prototype.open

  const patchedSend = function patchedSend(
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    try {
      if (tokenRequestDetailsByRequest.has(this)) {
        bodyTypeByRequest.set(this, getKeycloakTokenRequestBodyType(body))
        hasBodyByRequest.set(this, body != null)
        stageByRequest.set(this, getKeycloakTokenRequestStage(body))
        startedAtByRequest.set(this, new Date().toISOString())
        startedAtTimeByRequest.set(this, Date.now())
      }
    } catch {
      // A telemetria nunca deve interferir no fluxo de autenticação.
    }

    return originalSend.apply(this, arguments as any)
  } as typeof XMLHttpRequest.prototype.send

  XMLHttpRequest.prototype.open = patchedOpen
  XMLHttpRequest.prototype.send = patchedSend

  return () => {
    if (XMLHttpRequest.prototype.open === patchedOpen) {
      XMLHttpRequest.prototype.open = originalOpen
    }

    if (XMLHttpRequest.prototype.send === patchedSend) {
      XMLHttpRequest.prototype.send = originalSend
    }
  }
}

function normalizeKeycloakInitError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  return new Error('Keycloak initialization failed with non-Error rejection')
}

function isKeycloakThirdPartyCheckIframeTimeout(error: unknown) {
  if (error instanceof Error) {
    return error.message.includes(thirdPartyCheckIframeTimeoutMessage)
  }

  if (error == null || typeof error !== 'object') {
    return false
  }

  const safeErrorObject = error as Record<string, unknown>
  return (
    typeof safeErrorObject.error === 'string' &&
    safeErrorObject.error.includes(thirdPartyCheckIframeTimeoutMessage)
  )
}

function sanitizeKeycloakError(error: unknown) {
  if (error == null) {
    return { valueType: String(error) }
  }

  if (typeof error !== 'object') {
    return { valueType: typeof error, value: String(error).slice(0, 300) }
  }

  const safeErrorObject = error as Record<string, unknown>
  const sanitizedPayload: SanitizedKeycloakErrorPayload = {}

  safeKeycloakErrorFieldNames.forEach(fieldName => {
    const fieldValue = safeErrorObject[fieldName]

    if (typeof fieldValue === 'string') {
      sanitizedPayload[fieldName] = fieldValue.slice(0, 300)
      return
    }

    if (typeof fieldValue === 'number' || typeof fieldValue === 'boolean' || fieldValue === null) {
      sanitizedPayload[fieldName] = fieldValue
    }
  })

  return {
    valueType: 'object',
    keys: Object.keys(safeErrorObject).slice(0, 20),
    sanitizedPayload,
  }
}

function sanitizeKeycloakInitNonError(error: unknown) {
  return sanitizeKeycloakError(error)
}

function sanitizeKeycloakAuthenticationError(error: unknown) {
  if (error == null) {
    return { valueType: String(error) }
  }

  if (typeof error !== 'object') {
    return { valueType: typeof error }
  }

  return sanitizeKeycloakError(error)
}

function getSanitizedKeycloakErrorPayload(error: unknown): SanitizedKeycloakErrorPayload {
  const sanitizedError = sanitizeKeycloakError(error)

  if (sanitizedError.valueType !== 'object') {
    return {}
  }

  return sanitizedError.sanitizedPayload
}

function getSanitizedStringPayloadValue(
  payload: SanitizedKeycloakErrorPayload,
  fieldName: string,
) {
  const fieldValue = payload[fieldName]
  return typeof fieldValue === 'string' ? fieldValue : undefined
}

function getKeycloakTokenRequestPayload(telemetry: KeycloakTokenRequestTelemetry | null) {
  if (!telemetry) {
    return {}
  }

  return {
    tokenRequestDurationMilliseconds: telemetry.durationMilliseconds,
    tokenRequestFailureEventType: telemetry.failureEventType,
    tokenRequestOutcome: telemetry.outcome,
    tokenRequestRecordedEventType: telemetry.recordedEventType,
    tokenRequestStage: telemetry.stage,
    tokenRequestStatus: telemetry.status,
    tokenRequestStatusCategory: telemetry.statusCategory,
  }
}

function setKeycloakTokenRequestTelemetryOnScope(
  scope: Sentry.Scope,
  telemetry: KeycloakTokenRequestTelemetry | null,
) {
  scope.setTag('auth_token_request_outcome', telemetry?.outcome ?? 'not_observed')
  scope.setTag('auth_token_request_stage', telemetry?.stage ?? 'unknown')
  scope.setTag('auth_token_request_status_category', telemetry?.statusCategory ?? 'not_observed')

  if (telemetry?.status != null) {
    scope.setTag('auth_token_request_status', String(telemetry.status))
  }

  scope.setContext(
    'keycloak_token_request',
    telemetry
      ? {
          ...telemetry,
          observed: true,
        }
      : {
          observed: false,
        },
  )
}

function createKeycloakAuthenticationError(
  error: unknown,
  tokenRequestTelemetry: KeycloakTokenRequestTelemetry | null,
) {
  const sanitizedPayload = getSanitizedKeycloakErrorPayload(error)
  const errorDescription = getSanitizedStringPayloadValue(sanitizedPayload, 'error_description')
  const errorMessage = getSanitizedStringPayloadValue(sanitizedPayload, 'message')
  const errorReason = getSanitizedStringPayloadValue(sanitizedPayload, 'reason')
  const errorName =
    getSanitizedStringPayloadValue(sanitizedPayload, 'error') ??
    getSanitizedStringPayloadValue(sanitizedPayload, 'name')
  const tokenRequestPayload = getKeycloakTokenRequestPayload(tokenRequestTelemetry)
  const hasKeycloakAuthenticationErrorPayload =
    errorDescription != null || errorMessage != null || errorReason != null || errorName != null
  const authenticationErrorPayload = {
    errorDescription,
    message: errorDescription ?? errorMessage ?? errorReason,
    name: errorName,
    ...tokenRequestPayload,
  }

  if (hasKeycloakAuthenticationErrorPayload) {
    return errors.auth(
      errorName === 'access_denied' || errorName === 'login_required'
        ? keycloakAuthenticationDeniedMessage
        : keycloakAuthenticationReturnedErrorMessage,
      authenticationErrorPayload,
    )
  }

  return errors.auth(keycloakAuthenticationFallbackMessage, {
    name: 'keycloak_authentication_error_without_payload',
    ...tokenRequestPayload,
  })
}

function captureKeycloakAuthenticationError(
  error: unknown,
  initOptions: KeycloakInitOptions,
  tokenRequestTelemetry: KeycloakTokenRequestTelemetry | null,
) {
  Sentry.withScope(scope => {
    scope.setLevel('warning')
    scope.setTag('auth_stage', 'keycloak_auth_error')
    scope.setTag('auth_error_payload', error == null ? 'missing' : 'provided')
    scope.setContext('keycloak_init_options', getKeycloakInitContext(initOptions))
    setKeycloakTokenRequestTelemetryOnScope(scope, tokenRequestTelemetry)
    scope.setExtra('keycloak_authentication_error', sanitizeKeycloakAuthenticationError(error))
    Sentry.captureMessage('Keycloak authentication error')
  })
}

function captureKeycloakAuthenticationRefreshError(
  initOptions: KeycloakInitOptions,
  tokenRequestTelemetry: KeycloakTokenRequestTelemetry | null,
) {
  Sentry.withScope(scope => {
    scope.setLevel('warning')
    scope.setTag('auth_stage', 'keycloak_auth_refresh_error')
    scope.setContext('keycloak_init_options', getKeycloakInitContext(initOptions))
    setKeycloakTokenRequestTelemetryOnScope(scope, tokenRequestTelemetry)
    Sentry.captureMessage('Keycloak authentication refresh error')
  })
}

export function KeycloakProvider({
  children,
  keycloak,
  initOptions = defaultInitOptions,
  autoRefreshOnExpire = true,
  autoRefreshBeforeExpire = true,
  checkTokenSeconds = 30,
  refreshSecondsBeforeExpire = 60,
}: KeycloakProviderProps) {
  const [token, setToken] = useState<string | null>(null)
  const [tokenParsed, setTokenParsed] = useState<KeycloakTokenParsed | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [initError, setInitError] = useState<AppError | null>(null)
  const [authError, setAuthError] = useState<AppError | null>(null)
  const [authRefreshError, setAuthRefreshError] = useState<AppError | null>(null)
  const lastKeycloakAuthenticationErrorReference = useRef<AppError | null>(null)
  const lastKeycloakTokenRequestTelemetryReference = useRef<KeycloakTokenRequestTelemetry | null>(null)

  useEffect(() => {
    if (bypassAuth) {
      return
    }

    lastKeycloakTokenRequestTelemetryReference.current = null
    const uninstallKeycloakTokenRequestTelemetry = installKeycloakTokenRequestTelemetry(
      lastKeycloakTokenRequestTelemetryReference,
    )
    setAuthError(null)
    lastKeycloakAuthenticationErrorReference.current = null
    const handleUpdateAuth = () => {
      lastKeycloakAuthenticationErrorReference.current = null
      lastKeycloakTokenRequestTelemetryReference.current = null
      setAuthError(null)
      setAuthRefreshError(null)
      setToken(keycloak.token ?? null)
      setTokenParsed(keycloak.tokenParsed ?? null)
      setIsAuthenticated(keycloak.authenticated ?? null)
    }
    keycloak.onAuthRefreshSuccess = handleUpdateAuth
    keycloak.onAuthSuccess = handleUpdateAuth
    keycloak.onAuthLogout = handleUpdateAuth
    keycloak.onReady = handleUpdateAuth
    keycloak.onAuthError = (error: unknown) => {
      const keycloakAuthenticationError = createKeycloakAuthenticationError(
        error,
        lastKeycloakTokenRequestTelemetryReference.current,
      )
      lastKeycloakAuthenticationErrorReference.current = keycloakAuthenticationError
      captureKeycloakAuthenticationError(
        error,
        initOptions,
        lastKeycloakTokenRequestTelemetryReference.current,
      )
      setInitError(null)
      setAuthError(keycloakAuthenticationError)
      setToken(null)
      setTokenParsed(null)
      setIsAuthenticated(false)
    }
    keycloak.onAuthRefreshError = () => {
      captureKeycloakAuthenticationRefreshError(
        initOptions,
        lastKeycloakTokenRequestTelemetryReference.current,
      )
      setAuthRefreshError(
        errors.auth(
          keycloakRefreshErrorMessage,
          getKeycloakTokenRequestPayload(lastKeycloakTokenRequestTelemetryReference.current),
        ),
      )
    }
    keycloak.onTokenExpired = () => {
      if (autoRefreshOnExpire) {
        keycloak.updateToken(refreshSecondsBeforeExpire)
      }
    }

    return () => {
      keycloak.onAuthError = undefined
      keycloak.onAuthRefreshError = undefined
      keycloak.onTokenExpired = undefined
      keycloak.onAuthRefreshSuccess = undefined
      keycloak.onAuthSuccess = undefined
      keycloak.onAuthLogout = undefined
      keycloak.onReady = undefined
      uninstallKeycloakTokenRequestTelemetry()
    }
  }, [keycloak, initOptions, autoRefreshOnExpire, refreshSecondsBeforeExpire])

  useEffect(() => {
    setIsLoading(true)
    setInitError(null)
    setAuthError(null)
    setIsAuthenticated(null)
    lastKeycloakAuthenticationErrorReference.current = null
    lastKeycloakTokenRequestTelemetryReference.current = null

    if (bypassAuth) {
      const isBypassAuthenticated = !!localStorage.getItem('isBypassAuthenticated')

      if (isBypassAuthenticated) {
        setIsAuthenticated(isBypassAuthenticated)
        setToken('bypass')
        setTokenParsed(bypassAuthPayload)
        setIsLoading(false)
      } else {
        setIsAuthenticated(false)
        setToken(null)
        setTokenParsed(null)
        setIsLoading(false)
      }
      return
    }

    let isStopped = false
    keycloak
      .init(initOptions)
      .then(isAuthenticated => {
        if (isStopped) return
        setToken(keycloak.token ?? null)
        setTokenParsed(keycloak.tokenParsed ?? null)
        setIsAuthenticated(isAuthenticated)
      })
      .catch(error => {
        if (isStopped) return
        const normalizedError = normalizeKeycloakInitError(error)
        const keycloakInitContext = getKeycloakInitContext(initOptions)

        if (error == null && lastKeycloakAuthenticationErrorReference.current) {
          setToken(null)
          setTokenParsed(null)
          setIsAuthenticated(false)
          return
        }

        if (isKeycloakThirdPartyCheckIframeTimeout(error)) {
          Sentry.withScope(scope => {
            scope.setLevel('warning')
            scope.setTag('auth_stage', 'keycloak_init')
            scope.setTag('auth_fallback', 'third_party_check_iframe_timeout')
            scope.setContext('keycloak_init_options', keycloakInitContext)
            scope.setExtra('normalized_error_message', normalizedError.message)
            setKeycloakTokenRequestTelemetryOnScope(
              scope,
              lastKeycloakTokenRequestTelemetryReference.current,
            )

            if (!(error instanceof Error)) {
              scope.setExtra('non_error_exception', sanitizeKeycloakInitNonError(error))
            }

            Sentry.captureMessage('Keycloak third-party check iframe timeout ignored')
          })
          setAuthError(errors.auth(keycloakSessionCheckFallbackMessage))
          setToken(null)
          setTokenParsed(null)
          setIsAuthenticated(false)
          return
        }

        Sentry.withScope(scope => {
          scope.setTag('auth_stage', 'keycloak_init')
          scope.setContext('keycloak_init_options', keycloakInitContext)
          setKeycloakTokenRequestTelemetryOnScope(
            scope,
            lastKeycloakTokenRequestTelemetryReference.current,
          )

          if (!(error instanceof Error)) {
            scope.setExtra('non_error_exception', sanitizeKeycloakInitNonError(error))
          }

          Sentry.captureException(normalizedError)
        })
        setInitError(
          errors.unknown(keycloakInitializationErrorMessage, undefined, error),
        )
        setIsAuthenticated(false)
      })
      .finally(() => {
        if (isStopped) return
        setIsLoading(false)
      })

    return () => {
      isStopped = true
    }
  }, [keycloak, initOptions])

  useEffect(() => {
    if (!tokenParsed || !autoRefreshBeforeExpire || bypassAuth) {
      return
    }

    const interval = setInterval(() => {
      keycloak.updateToken(refreshSecondsBeforeExpire)
    }, checkTokenSeconds * 1000)
    return () => clearInterval(interval)
  }, [tokenParsed, checkTokenSeconds, autoRefreshBeforeExpire, refreshSecondsBeforeExpire])

  const login = useCallback(() => {
    if (bypassAuth) {
      localStorage.setItem('isBypassAuthenticated', 'true')
      setIsAuthenticated(true)
      setToken('bypass')
      setTokenParsed(bypassAuthPayload)
      return Promise.resolve()
    }

    return keycloak.login()
  }, [keycloak])
  const logout = useCallback(() => {
    if (bypassAuth) {
      localStorage.removeItem('isBypassAuthenticated')
      setIsAuthenticated(false)
      setToken(null)
      setTokenParsed(null)
      return clear()
    }

    const keycloakLogout = keycloak.logout

    return clear()
      .catch(() => {})
      .then(() => keycloakLogout())
  }, [keycloak])

  const error = authError ?? initError ?? authRefreshError ?? null

  const context: KeycloakContext = useMemo(() => {
    return {
      login,
      logout,
      keycloak,
      bypassAuth,
      isLoading,
      isAuthenticated,
      error,
      token,
      tokenParsed,
      initError,
      authError,
      authRefreshError,
    }
  }, [
    login,
    logout,
    keycloak,
    bypassAuth,
    isLoading,
    isAuthenticated,
    error,
    token,
    tokenParsed,
    initError,
    authError,
    authRefreshError,
  ])

  return <keycloakContext.Provider value={context}>{children}</keycloakContext.Provider>
}

export function useKeycloak() {
  return useContext(keycloakContext)
}
